import { useSyncExternalStore } from 'react';

/** localStorage key tracking whether a shopper has already seen the compare hint. */
export const COMPARE_HINT_SEEN_KEY = 'jane:compare-hint-seen';

const hasSeenHint = (): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(COMPARE_HINT_SEEN_KEY) === 'true';
  } catch {
    // Private mode / storage disabled — fail open and show the hint.
    return false;
  }
};

const persistHintSeen = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COMPARE_HINT_SEEN_KEY, 'true');
  } catch {
    // Storage unavailable — the hint just won't be remembered.
  }
};

interface UseCompareHintResult {
  /** True once the shopper has seen the hint (persisted per browser). */
  hintWasSeen: boolean;
  /** Persist the "seen" flag. Safe to call repeatedly. */
  markHintSeen: () => void;
}

// Value never changes within a session, so there's nothing to subscribe to.
const subscribe = () => () => {};

/**
 * Reads the "seen" flag in an SSR-safe way. The server snapshot is always `true`
 * (Bloom SSR has no localStorage, so nothing renders); the client re-reads after
 * hydration. This keeps the first client render matching the server and avoids a
 * hydration mismatch that would break surrounding interactivity.
 */
export const useCompareHint = (): UseCompareHintResult => {
  const hintWasSeen = useSyncExternalStore(subscribe, hasSeenHint, () => true);

  return { hintWasSeen, markHintSeen: persistHintSeen };
};
